
import { useState, useEffect, useRef, useContext } from 'react';
import DebitNoteScroll from './DebitNoteScroll';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import GeneralModal from '../../helpers/GeneralModal';
import { createModal } from 'react-modal-promise';
import LogicContext from '../../Context/LogicContext';
import { SetEditDebitNoteAtom, GetDebitNoteListAtom, SetDebitNoteListAtom, DebitNoteState } from '../../store/debitNoteStore';
import { ImAttachment } from 'react-icons/im';
import { FaFilePdf } from 'react-icons/fa';
import { MdDelete, MdEdit } from 'react-icons/md';
import { IoPrintSharp } from 'react-icons/io5';
import DebitNoteDetailsContent from './DebitNoteDetailsContent';
import { useReactToPrint } from 'react-to-print';
import DebitNoteFilter from './DebitNoteFilter';
import DebitNoteOptions from './DebitNoteOptions';
import { DebitNote, DebitNoteDetails, AccountInfo } from './interface/DebiitNote.Interface';
import { toLowercaseKeys } from '../../helpers/toLowerCaseKeys';
import { debitNoteService } from '../../services/debitNote.service';

// Types

interface StatusInfo {
  [key: string]: any;
}

interface StatusSelection {
  debit_note_status_id: number;
  debit_note_status_name: string;
}

interface ModalResponse {
  decision: boolean;
}

// interface ApiResponse {
//   data: DebitNoteDetails[] | DebitNoteState;
// }

interface LogicContextType {
  showToast: (message: string, type: string) => void;
}

function DebitNoteDetailsPage() {
  const myPromiseModal = createModal(GeneralModal);
  const setDebitNoteEdit = SetEditDebitNoteAtom();
  const getDebitNoteListAtom: DebitNoteState = GetDebitNoteListAtom();
  const setDebitNoteListAtom = SetDebitNoteListAtom();

  const logicContext = useContext(LogicContext) as LogicContextType;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const componentRef = useRef<HTMLDivElement>(null);
  const url = pathname.split('/');
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];
  const [debitNoteDetails, setDebitNoteDetails] = useState<DebitNoteDetails>({} as DebitNoteDetails);
  const [statusInfo, setStatusInfo] = useState<StatusInfo[]>([] as StatusInfo[]);
  const [accountInfo, setAccountInfo] = useState<AccountInfo[]>([]);

  useEffect(() => {
    let isMounted = true;
    if (id) {
      loadDetails(id, isMounted);
    }
    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    document.title = 'Debit note page';
    if (!getDebitNoteListAtom.debit_note?.length) {
      loadTableList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDebitNoteListAtom.debit_note]);

  const loadTableList = (): void => {
    debitNoteService.debitNoteList().then((res: any) => {
      const serverData = toLowercaseKeys(res.data);
      if (serverData) {
        setDebitNoteListAtom(serverData as DebitNoteState);
      }
    });
  };

  const loadDetails = async (noteId: string, isMounted: boolean = true): Promise<void> => {
    try {
      const response: any = await debitNoteService.debitNoteDetails(noteId);
      const serverData = toLowercaseKeys(response?.data);
      if (Array.isArray(serverData?.debit_note) && (serverData?.debit_note as DebitNoteDetails[]).length) {
        const debitNote = (serverData?.debit_note as DebitNoteDetails[])[0];
        const tmpStatusInfo = serverData?.status_info;
        const tmpAccountInfo = serverData?.account_info || [];
        if (isMounted) {
          setDebitNoteDetails(debitNote);
          setStatusInfo(tmpStatusInfo?.length ? tmpStatusInfo: []);
          setAccountInfo(tmpAccountInfo);
        }
        const newList = getDebitNoteListAtom?.debit_note.map((item: any) => {
          if (item.ref_no === debitNote.ref_no) {
            return { ...item, ...debitNote };
          } else {
            return item;
          }
        });
        setDebitNoteListAtom({ debit_note: newList, status_info: getDebitNoteListAtom.status_info });
      }
    } catch (error) {
      console.log('Error ', error);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const addNewRecord = (): void => {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
  };

  const viewCustomerDetails = (customerId: string): void => {
    const customerRoute = `/${currModuleId}/${currModuleName}/customers/customer-details/${customerId}`;
    // Option A: Open in new tab using window.open()
    window.open(customerRoute, '_blank');
  };

  const editCreditNoteOrder = async (): Promise<void> => {
    const response = (await myPromiseModal({
      title: 'Modify Credit Note',
      body: ' Do you want to modify this record?',
      showInput: false,
      placeHolder: '',
    })) as ModalResponse;

    if (response.decision === true) {
      setDebitNoteEdit(debitNoteDetails);
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
    }
  };

  const onStatusChange = async (selection: StatusSelection): Promise<void> => {
    if (!id) return;

    const formData = {
      ref_no: id,
      option: selection?.debit_note_status_id,
    };
    const response = (await myPromiseModal({
      title: `Change status to ${selection?.debit_note_status_name}`,
      body: ' Do you want to modify this record?',
      showInput: false,
      placeHolder: '',
    })) as ModalResponse;

    if (response?.decision === true) {
      debitNoteService
        .debitNoteStatus(formData)
        .then((res: any) => {
          loadDetails(id);
          const responseData = res.data;

          if (responseData !== undefined) {
            const { message_text: Message_text, message_id } = responseData?.[0] || res.data?.result_status?.[0];
            if (message_id == 1) {
              logicContext.showToast(Message_text, 'success');
            } else {
              logicContext.showToast(Message_text || 'Action failed', 'error');
            }
          }
        })
        .catch(err => {
          console.error('Error changing status:', err);
        });
    }
  };


  return (
    <>
      <section className="">
        <section className="flex flex-wrap justify-between">
          <div className="mt-3"></div>

          <div className="flex flex-row flex-wrap items-center">
            <div className="mt-5 flex flex-row items-center mr-6">
              <button onClick={addNewRecord} className="btn btn-primary m-4">
                Add New
              </button>

              {statusInfo?.length > 0 && <DebitNoteOptions bulkActionData={statusInfo} onFilterSelect={onStatusChange} />}
            </div>
            <div className="mt-3 flex fle-row items-center space-x-1 ">
              <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
                <ImAttachment className="h-[20px] w-[20px] flex-shrink-0 text-gray-600" />
              </div>

              <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
                <FaFilePdf onClick={handlePrint} className="h-[20px] w-[20px] flex-shrink-0 text-gray-600" />
              </div>

              <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
                <IoPrintSharp onClick={handlePrint} className="h-[23px] w-[23px] flex-shrink-0 text-gray-600" />
              </div>
              <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
                <MdEdit onClick={editCreditNoteOrder} className="h-[23px] w-[23px] flex-shrink-0 text-gray-600" />
              </div>
              {/* <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
                <MdDelete className="h-[23px] w-[23px] flex-shrink-0 text-gray-600" />
              </div> */}
            </div>
          </div>
        </section>
        <section className="mt-4 lg:flex space-x-1">
          <div className="lg:w-[210px]">
            <DebitNoteScroll></DebitNoteScroll>
          </div>
          <section className="w-full overflow-x-hidden mt-4 lg:mt-0 lg:pl-4 ">
            {/* Paste here */}

            <DebitNoteDetailsContent
              viewCustomerDetails={viewCustomerDetails}
              ref={componentRef}
              id={id || ''}
              creditNoteDetails={debitNoteDetails}
              accountInfo={accountInfo}
            />
            {/* Paste end */}
          </section>
        </section>
      </section>
    </>
  );
}

export default DebitNoteDetailsPage;
